import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Loader2, Pause, Play, ShieldAlert, Waves } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { unlockAudioContext } from '../utils/listenPlayback';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const S1_ENABLED = process.env.REACT_APP_MESSAGE_TTS_STREAM_SPIKE === 'true';
const OUTPUT_VOLUME = 0.7;

const sha256 = async (buffer) => {
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
};

const decodeMp3 = (context, buffer) => new Promise((resolve, reject) => {
  const result = context.decodeAudioData(buffer, resolve, reject);
  if (result && typeof result.then === 'function') result.then(resolve, reject);
});

export default function S1OutputABDiagnostic() {
  const navigate = useNavigate();
  const [authority, setAuthority] = useState('checking');
  const [text, setText] = useState('');
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [evidence, setEvidence] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const sourceRef = useRef(null);
  const contextRef = useRef(null);
  const decodedRef = useRef(null);
  const blobUrlRef = useRef(null);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch (ignored) { /* already stopped */ }
      sourceRef.current = null;
    }
    setPlaying(null);
  }, []);

  useEffect(() => {
    let alive = true;
    if (!S1_ENABLED) {
      setAuthority('denied');
      return undefined;
    }
    fetch(`${API_URL}/api/founder/access`, { credentials: 'include' })
      .then((response) => {
        if (alive) setAuthority(response.ok ? 'allowed' : 'denied');
      })
      .catch(() => {
        if (alive) setAuthority('denied');
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch (ignored) { /* already stopped */ }
    }
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
  }, []);

  const prepareExactBytes = async () => {
    const sourceText = text.trim();
    if (!sourceText || phase !== 'idle') return;
    setPhase('preparing');
    setError('');
    stopPlayback();

    try {
      const context = unlockAudioContext();
      if (!context) throw new Error('Web Audio is unavailable on this device.');
      contextRef.current = context;

      const prepared = await fetch(`${API_URL}/api/companion/tts/stream/prepare`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: sourceText, language: 'en' })
      });
      if (!prepared.ok) throw new Error(`Preparation failed (${prepared.status}).`);
      const ticket = await prepared.json();
      if (ticket.mode !== 'buffered') throw new Error(`Expected buffered mode; received ${ticket.mode}.`);

      const media = await fetch(`${API_URL}/api/companion/tts/stream?t=${encodeURIComponent(ticket.ticket)}`, {
        credentials: 'include'
      });
      if (!media.ok) throw new Error(`MP3 load failed (${media.status}).`);
      const bytes = await media.arrayBuffer();
      const contentLength = Number(media.headers.get('content-length') || 0);
      if (!bytes.byteLength || contentLength !== bytes.byteLength) {
        throw new Error('The complete MP3 was not loaded into memory.');
      }

      const hash = await sha256(bytes.slice(0));
      const blob = new Blob([bytes.slice(0)], { type: 'audio/mpeg' });
      const blobBytes = await blob.arrayBuffer();
      const elementHash = await sha256(blobBytes.slice(0));
      const webAudioInput = bytes.slice(0);
      const webAudioHash = await sha256(webAudioInput.slice(0));
      if (elementHash !== hash || webAudioHash !== hash) {
        throw new Error('The two playback arms do not share identical bytes.');
      }

      decodedRef.current = await decodeMp3(context, webAudioInput);
      blobUrlRef.current = URL.createObjectURL(blob);
      setEvidence({
        hash,
        bytes: bytes.byteLength,
        chars: ticket.chars,
        engine: ticket.engine,
        elementHash,
        webAudioHash
      });
      setPhase('ready');
    } catch (problem) {
      setError(problem?.message || 'The diagnostic could not be prepared.');
      setPhase('idle');
    }
  };

  const playElement = async () => {
    if (phase !== 'ready' || !blobUrlRef.current) return;
    stopPlayback();
    const audio = new Audio(blobUrlRef.current);
    audio.preload = 'auto';
    audio.playsInline = true;
    audio.volume = OUTPUT_VOLUME;
    audio.onended = () => setPlaying(null);
    audio.onerror = () => {
      setPlaying(null);
      setError('Arm A encountered an HTML audio playback error.');
    };
    audioRef.current = audio;
    setPlaying('A');
    try {
      await audio.play();
    } catch (problem) {
      setPlaying(null);
      setError(problem?.message || 'Arm A could not start.');
    }
  };

  const playWebAudio = async () => {
    if (phase !== 'ready' || !decodedRef.current || !contextRef.current) return;
    stopPlayback();
    const context = contextRef.current;
    try {
      if (context.state === 'suspended') await context.resume();
      const gain = context.createGain();
      gain.gain.value = OUTPUT_VOLUME;
      gain.connect(context.destination);
      const source = context.createBufferSource();
      source.buffer = decodedRef.current;
      source.connect(gain);
      source.onended = () => {
        sourceRef.current = null;
        setPlaying(null);
      };
      sourceRef.current = source;
      setPlaying('B');
      source.start(context.currentTime + 0.02);
    } catch (problem) {
      setPlaying(null);
      setError(problem?.message || 'Arm B could not start.');
    }
  };

  if (authority === 'checking') {
    return (
      <main className="min-h-screen bg-[#080b0d] text-white grid place-items-center" data-testid="s1-ab-authority-check">
        <Loader2 className="h-7 w-7 animate-spin text-[#d7b56d]" />
      </main>
    );
  }

  if (authority !== 'allowed') {
    return (
      <main className="min-h-screen bg-[#080b0d] text-white grid place-items-center px-6" data-testid="s1-ab-denied-page">
        <div className="max-w-md text-center">
          <ShieldAlert className="mx-auto mb-5 h-9 w-9 text-[#d7b56d]" />
          <h1 className="text-3xl font-serif" data-testid="s1-ab-denied-title">Not available</h1>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#080b0d] text-white px-5 py-8 sm:px-8" data-testid="s1-output-ab-page">
      <div className="mx-auto max-w-3xl">
        <button
          type="button"
          onClick={() => navigate('/companion')}
          className="mb-12 inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-white"
          data-testid="s1-ab-back-button"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-10 border-b border-white/10 pb-10">
          <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#d7b56d]">
            <Waves className="h-4 w-4" />
            Founder DEV diagnostic
          </div>
          <h1 className="text-4xl font-serif sm:text-5xl" data-testid="s1-ab-heading">Exact-byte output A/B</h1>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-white/55" data-testid="s1-ab-purpose">
            One continuous MP3 is loaded once into memory. Both controls play audio derived from that same SHA-256-verified byte array.
          </p>
        </header>

        {phase === 'idle' && (
          <section className="space-y-5" data-testid="s1-ab-source-section">
            <label className="block text-sm text-white/75" htmlFor="s1-source-message">Source Message</label>
            <textarea
              id="s1-source-message"
              value={text}
              onChange={(event) => setText(event.target.value)}
              maxLength={4000}
              rows={8}
              className="w-full resize-y rounded-md border border-white/15 bg-white/[0.04] p-4 text-base leading-7 text-white outline-none transition-colors focus:border-[#d7b56d]/70"
              data-testid="s1-ab-source-input"
            />
            <button
              type="button"
              onClick={prepareExactBytes}
              disabled={!text.trim()}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-[#d7b56d] px-6 font-semibold text-[#11100d] transition-[opacity,transform] hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-35"
              data-testid="s1-ab-prepare-button"
            >
              Generate one MP3
            </button>
          </section>
        )}

        {phase === 'preparing' && (
          <section className="flex min-h-48 items-center gap-4 border-y border-white/10" data-testid="s1-ab-preparing-state">
            <Loader2 className="h-6 w-6 animate-spin text-[#d7b56d]" />
            <span className="text-white/70">Generating and verifying one in-memory MP3…</span>
          </section>
        )}

        {phase === 'ready' && evidence && (
          <section className="space-y-10" data-testid="s1-ab-ready-state">
            <div className="grid gap-px overflow-hidden rounded-md border border-white/10 bg-white/10 sm:grid-cols-3">
              <div className="bg-[#0d1114] p-4">
                <div className="text-xs uppercase tracking-wider text-white/40">Bytes</div>
                <div className="mt-2 font-mono text-sm" data-testid="s1-ab-byte-count">{evidence.bytes.toLocaleString()}</div>
              </div>
              <div className="bg-[#0d1114] p-4">
                <div className="text-xs uppercase tracking-wider text-white/40">Engine</div>
                <div className="mt-2 font-mono text-sm" data-testid="s1-ab-engine">{evidence.engine}</div>
              </div>
              <div className="bg-[#0d1114] p-4">
                <div className="text-xs uppercase tracking-wider text-white/40">Byte identity</div>
                <div className="mt-2 flex items-center gap-2 text-sm text-[#7dd3ae]" data-testid="s1-ab-identity-confirmation">
                  <Check className="h-4 w-4" /> A = B
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 text-xs uppercase tracking-wider text-white/40">One SHA-256</div>
              <code className="block break-all rounded-md bg-black/30 p-4 text-xs leading-6 text-white/65" data-testid="s1-ab-sha256">{evidence.hash}</code>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <button
                type="button"
                onClick={playing === 'A' ? stopPlayback : playElement}
                className="min-h-28 rounded-md border border-[#d7b56d]/45 bg-[#d7b56d]/10 p-5 text-left transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#d7b56d]/15"
                data-testid="s1-ab-play-element-button"
              >
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#d7b56d]">A — HTMLAudioElement</span>
                <span className="mt-4 flex items-center gap-2 text-base font-semibold">
                  {playing === 'A' ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                  {playing === 'A' ? 'Stop A' : 'Play A'}
                </span>
              </button>

              <button
                type="button"
                onClick={playing === 'B' ? stopPlayback : playWebAudio}
                className="min-h-28 rounded-md border border-[#65c7b0]/45 bg-[#65c7b0]/10 p-5 text-left transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#65c7b0]/15"
                data-testid="s1-ab-play-webaudio-button"
              >
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#79d8c2]">B — AudioContext</span>
                <span className="mt-4 flex items-center gap-2 text-base font-semibold">
                  {playing === 'B' ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                  {playing === 'B' ? 'Stop B' : 'Play B'}
                </span>
              </button>
            </div>

            <p className="text-xs leading-6 text-white/45" data-testid="s1-ab-offline-confirmation">
              Ready in memory. Playing A or B performs no provider call, media fetch, ticket request, or regeneration.
            </p>
          </section>
        )}

        {error && (
          <div className="mt-6 rounded-md border border-red-400/25 bg-red-400/10 p-4 text-sm text-red-100" role="alert" data-testid="s1-ab-error">
            {error}
          </div>
        )}
      </div>
    </main>
  );
}